import { cva, type VariantProps } from 'class-variance-authority'

export { default as Button } from './Button.vue'

const variant_library = new Map<string, string>();
variant_library.set("default", "tw-bg-primary tw-text-primary-foreground tw-shadow hover:tw-bg-primary/90");
variant_library.set("secondary", "tw-bg-[hsl(var(--input-background))] tw-text-secondary-foreground tw-shadow-sm hover:tw-bg-secondary/80");
variant_library.set("success", "tw-bg-green-500 tw-text-white tw-shadow-sm hover:tw-bg-green-800");
variant_library.set("destructive", "tw-bg-destructive tw-text-destructive-foreground tw-shadow-sm hover:tw-bg-destructive/90");
variant_library.set("ghost", "hover:tw-bg-accent hover:tw-text-accent-foreground");
variant_library.set("link", "tw-text-primary tw-underline-offset-4 hover:tw-underline");
variant_library.set("highlight", "tw-bg-cyan-500 tw-text-black tw-shadow-sm hover:tw-bg-cyan-800");

variant_library.set("default_outline", "tw-border tw-border-primary tw-bg-[hsl(var(--input-background))] tw-shadow-sm hover:tw-bg-primary/90 hover:tw-text-primary-foreground");
variant_library.set("secondary_outline", "tw-border tw-border-input tw-bg-[hsl(var(--input-background))] tw-shadow-sm hover:tw-bg-accent hover:tw-text-accent-foreground");
variant_library.set("success_outline", "tw-border tw-border-green-500 tw-bg-background tw-shadow-sm hover:tw-bg-green-800 hover:tw-text-green-300");
variant_library.set("destructive_outline", "tw-border tw-border-destructive tw-bg-background tw-shadow-sm hover:tw-bg-destructive/90 hover:tw-text-destructive-foreground");
variant_library.set("destructive_outline_text", "tw-border tw-border-destructive tw-bg-background tw-shadow-sm hover:tw-bg-destructive/90 hover:tw-text-destructive-foreground");
variant_library.set("highlight_outline", "tw-border tw-border-cyan-500 tw-bg-background tw-shadow-sm hover:tw-bg-cyan-800 hover:tw-text-white");

variant_library.set("outline", "tw-border tw-border-input tw-bg-background tw-shadow-sm hover:tw-bg-accent hover:tw-text-accent-foreground");

variant_library.set("default_outline_text", variant_library.get("default_outline")! + " tw-text-primary-foreground hover:tw-text-white");
variant_library.set("secondary_outline_text", variant_library.get("secondary_outline")! + " tw-text-accent-foreground hover:tw-text-white");
variant_library.set("success_outline_text", variant_library.get("success_outline")! + " tw-text-green-300 hover:tw-text-white");
variant_library.set("destructive_outline_text", variant_library.get("destructive_outline")! + " tw-text-destructive-foreground hover:tw-text-white");
variant_library.set("highlight_outline_text", variant_library.get("highlight_outline")! + " tw-text-cyan-500 hover:tw-text-white");

export const buttonVariants = cva(
  'tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-text-sm tw-font-bold tw-ring-offset-background tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2 disabled:tw-pointer-events-none disabled:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg]:tw-size-4 [&_svg]:tw-shrink-0',
  {
    variants: {
      variant: {
        // Filled
        default: variant_library.get("default"),
        secondary: variant_library.get("secondary"),
        success: variant_library.get("success"),
        destructive: variant_library.get("destructive"),
        ghost: variant_library.get("ghost"),
        link: variant_library.get("link"),
        highlight: variant_library.get("highlight"),

        // Outlines
        default_outline: variant_library.get("default_outline"),
        secondary_outline: variant_library.get("secondary_outline"),
        success_outline: variant_library.get("success_outline"),
        destructive_outline: variant_library.get("destructive_outline"),
        highlight_outline: variant_library.get("highlight_outline"),

        // Outlines + Text Color (outlines where text color is same as border color)
        default_outline_text: variant_library.get("default_outline_text"),
        secondary_outline_text: variant_library.get("secondary_outline_text"),
        success_outline_text: variant_library.get("success_outline_text"),
        destructive_outline_text: variant_library.get("destructive_outline_text"),
        highlight_outline_text: variant_library.get("highlight_outline_text"),

        // DEPRECATED
        outline: variant_library.get("outline"), // here for backwards compatibility
      },
      size: {
        default: 'tw-h-9 tw-px-4 tw-py-2',
        sm: 'tw-h-8 tw-rounded-md tw-px-3 tw-text-xs',
        lg: 'tw-h-10 tw-rounded-md tw-px-8',
        icon: 'tw-h-9 tw-w-9',
        none: "",
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export type ButtonVariants = VariantProps<typeof buttonVariants>
